import React from 'react'

import Status from '@components/Status'
import { QuestionAiReview } from '@models/aiGeneration.model'

// O que a curadoria precisa ver antes de aprovar uma questão vinda da IA.
//
// Uma fundamentação escrita de memória é visualmente idêntica a uma transcrita
// da lei, e um gabarito deduzido é idêntico a um que a banca publicou. Sem
// marcar a diferença, aprovar as vinte questões de um caderno vira uma decisão
// tomada às cegas — e o aluno decora o oposto do que a lei diz.
//
// Os selos são separados da ressalva escrita: o selo se lê de relance numa
// lista de vinte; o texto explica o que fazer quando o selo acende.
//
// Serve as duas telas — a lista da geração e a questão aberta — porque o
// julgamento é o mesmo nas duas, e duplicá-lo deixaria uma delas para trás na
// primeira vez que um alerta novo aparecesse.

interface Props {
	review: QuestionAiReview

	// Lidos da questão como ela ficou no acervo, e não do que a IA enviou: é o
	// que está gravado que a curadoria vai aprovar.
	title?: string | null
	explanation?: string | null
	subMatterCount?: number

	// 'inline' cabe numa célula de tabela; 'panel' é o bloco da questão aberta.
	variant?: 'inline' | 'panel'
}

type Tone = 'success' | 'warn' | 'danger'

const styles = {
	inline: 'flex flex-col gap-2',
	panel: 'flex flex-col gap-3 bg-white rounded-sm shadow-5 px-6 py-5',
	panelTitle: 'text-base font-medium text-dark',
	badges: 'flex flex-wrap gap-x-2 gap-y-2',
	notes: 'flex flex-col gap-2',
	note: 'text-sm text-dark leading-relaxed pl-3 border-l-2 border-gray-200',
	facts: 'flex flex-col gap-1',
	factLabel: 'block text-xs uppercase tracking-wide text-gray-400',
	reference: 'text-sm text-gray',
}

export default function ReviewAlerts({
	review,
	title,
	explanation,
	subMatterCount = 0,
	variant = 'inline',
}: Props) {
	/*
	 * O booleano chega do MySQL como 0 ou 1. Comparado direto num `&&` de JSX,
	 * o zero não some da tela: ele é renderizado, e aparecia um "0" solto ao
	 * lado dos selos.
	 */
	const fromCorpus = Boolean(review.foundationFromCorpus)

	const hasNoSubMatter = subMatterCount === 0
	const hasNoTitle = !title
	const hasNoFoundation = !explanation?.trim()

	const badges: Array<{ label: string; tone: Tone }> = [
		...(!fromCorpus
			? [{ label: 'Lei não conferida', tone: 'danger' as Tone }]
			: []),
		...(hasNoFoundation
			? [{ label: 'Sem fundamentação', tone: 'danger' as Tone }]
			: []),
		...(hasNoSubMatter
			? [{ label: 'Sem subassunto', tone: 'warn' as Tone }]
			: []),
		...(hasNoTitle ? [{ label: 'Sem título', tone: 'warn' as Tone }] : []),
	]

	/*
	 * "Revisão apontou" só entra quando nenhum outro selo explica a ressalva.
	 * Ao lado de "Sem subassunto", ele repetiria o que já está dito e faria a
	 * lista parecer mais grave do que é.
	 */
	if (badges.length === 0 && review.reviewNote) {
		badges.push({ label: 'Revisão apontou', tone: 'warn' })
	}

	if (badges.length === 0) {
		badges.push({ label: 'Sem ressalvas', tone: 'success' })
	}

	/*
	 * A conversão manda uma ressalva por linha. São avisos independentes — a lei
	 * não conferida, o gabarito deduzido, o trecho suprimido —, e num parágrafo
	 * único o segundo se lê como continuação do primeiro.
	 */
	const notes = (review.reviewNote ?? '')
		.split('\n')
		.map((note) => note.trim())
		.filter((note) => note.length > 0)

	/*
	 * Duas informações diferentes, e por isso duas linhas com nome.
	 *
	 * A lei que fundamenta e o assunto onde a questão foi arquivada costumam
	 * citar artigos da mesma Constituição. Emendados por um ponto, não dá para
	 * saber onde um termina: "Art. 66 [...], CF de 1988 · Poder Legislativo
	 * (Arts. 44 - 75, CF)" se lê como uma citação só.
	 */
	const foundation = [review.referenceArticle, review.referenceLaw]
		.filter(Boolean)
		.join(', ')

	const subject = [review.subjectName, review.subSubjectName]
		.filter(Boolean)
		.join(' › ')

	return (
		<div className={variant === 'panel' ? styles.panel : styles.inline}>
			{variant === 'panel' && (
				<h2 className={styles.panelTitle}>Pontos de atenção da conversão</h2>
			)}

			<div className={styles.badges}>
				{badges.map((badge) => (
					<Status
						key={badge.label}
						status={badge.tone}
						className='px-3 py-[6px] text-sm'
					>
						{badge.label}
					</Status>
				))}
			</div>

			{notes.length > 0 && (
				<div className={styles.notes}>
					{notes.map((note) => (
						<p key={note} className={styles.note}>
							{note}
						</p>
					))}
				</div>
			)}

			{(foundation || subject) && (
				<div className={styles.facts}>
					{foundation && (
						<p className={styles.reference}>
							<span className={styles.factLabel}>Fundamento</span>
							{foundation}
						</p>
					)}

					{subject && (
						<p className={styles.reference}>
							<span className={styles.factLabel}>Assunto</span>
							{subject}
						</p>
					)}
				</div>
			)}

			{variant === 'panel' && (
				<p className={styles.reference}>
					Questão {review.sourceNumber ?? '—'} do caderno · custo{' '}
					{`US$ ${Number(review.costUsd).toFixed(3)}`}
				</p>
			)}
		</div>
	)
}
